import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import { useTranslation } from '../../../hooks/useTranslation';

const BuySugarModal = ({ load, onClose, onConfirm, forTruck = false }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  // Use string state so backspace/delete works freely
  const [quantityInput, setQuantityInput] = useState(String(load?.quantity || ''));
  const [insuranceAdded, setInsuranceAdded] = useState(false);
  const [quantityError, setQuantityError] = useState('');
  const [touched, setTouched] = useState(false);

  const selectedQuantity = parseFloat(quantityInput) || 0;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    })?.format(amount);
  };

  const validateQuantity = (val) => {
    const num = parseFloat(val);
    if (!val || val?.trim() === '' || isNaN(num) || num <= 0) {
      return 'Quantity must be greater than 0';
    }
    if (num > load?.quantity) {
      return `Maximum available quantity is ${load?.quantity} MT`;
    }
    return '';
  };

  const handleQuantityChange = (e) => {
    const val = e?.target?.value;
    // Allow free typing — only digits and one decimal point
    if (val === '' || /^\d*\.?\d*$/?.test(val)) {
      setQuantityInput(val);
      // Only show error if user has already blurred once
      if (touched) {
        setQuantityError(validateQuantity(val));
      } else {
        setQuantityError('');
      }
    }
  };

  const handleBlur = () => {
    setTouched(true);
    setQuantityError(validateQuantity(quantityInput));
  };

  const handleDecrement = () => {
    const current = parseFloat(quantityInput) || 0;
    const next = Math.max(1, current - 1);
    setQuantityInput(String(next));
    setQuantityError(validateQuantity(String(next)));
  };

  const handleIncrement = () => {
    const current = parseFloat(quantityInput) || 0;
    const next = Math.min(load?.quantity || Infinity, current + 1);
    setQuantityInput(String(next));
    setQuantityError(validateQuantity(String(next)));
  };

  const handleQuickSelect = (qty) => {
    setQuantityInput(String(qty));
    setQuantityError('');
    setTouched(true);
  };

  const handleProceedToReview = () => {
    setTouched(true);
    const err = validateQuantity(quantityInput);
    if (err) {
      setQuantityError(err);
      return;
    }
    // For truck flow: skip review step, go directly to truck modal
    if (forTruck) {
      onConfirm({ ...load, selectedQuantity, insuranceAdded, totalAmount });
      return;
    }
    setStep(2);
  };

  // Calculate amounts with selected quantity
  const subtotal = load?.rate * selectedQuantity;
  const gstRate = 0.05;
  const gstAmount = Math.round(subtotal * gstRate);
  const transitInsuranceRate = 0.005;
  const transitInsuranceAmount = Math.round(subtotal * transitInsuranceRate);
  const totalAmount = subtotal + gstAmount + (insuranceAdded ? transitInsuranceAmount : 0);

  const handleInsuranceToggle = () => {
    setInsuranceAdded(!insuranceAdded);
  };

  const handleConfirm = () => {
    onConfirm({ ...load, selectedQuantity, insuranceAdded, totalAmount });
  };

  const handleBack = () => {
    setStep(1);
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-md z-[1300] flex items-center justify-center p-4 animate-fadeIn">
      <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl max-w-3xl w-full border-2 border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800 border-b-2 border-gray-200 dark:border-gray-700 px-8 py-5">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                {step === 1 ? (forTruck ? 'Select Quantity (MT)' : 'Select Quantity') : 'Review Purchase'}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {step === 1
                  ? (forTruck ? 'Choose how much sugar you want to book with truck' : 'Choose how much sugar you want to purchase')
                  : 'Confirm your order details'}
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 flex items-center justify-center bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-full transition-all duration-200 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
              aria-label="Close modal"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-8 max-h-[calc(90vh-200px)] overflow-y-auto">
          {step === 1 ? (
            <div className="space-y-6">
              {/* Product Info */}
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
                <h3 className="text-base font-bold text-gray-900 dark:text-white mb-4">Product Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm text-gray-600 dark:text-gray-400">Mill Name:</span>
                    <p className="text-base font-semibold text-gray-900 dark:text-white">{load?.millName}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600 dark:text-gray-400">Offer ID:</span>
                    <p className="text-base font-semibold text-blue-600 dark:text-blue-400">{load?.offerId}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600 dark:text-gray-400">Product:</span>
                    <p className="text-base font-semibold text-gray-900 dark:text-white">{load?.product}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600 dark:text-gray-400">Grade:</span>
                    <p className="text-base font-semibold text-gray-900 dark:text-white">{load?.grade}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600 dark:text-gray-400">Rate per Ton:</span>
                    <p className="text-base font-bold text-green-600 dark:text-green-400">{formatCurrency(load?.rate)}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600 dark:text-gray-400">Available:</span>
                    <p className="text-base font-bold text-gray-900 dark:text-white">{load?.quantity} MT</p>
                  </div>
                </div>
              </div>

              {/* Quantity Input */}
              <div className="space-y-4">
                <div>
                  <label className="block text-base font-bold text-gray-900 dark:text-white mb-3">
                    Enter Quantity (MT)
                  </label>
                  {/* +/- stepper row */}
                  <div className="flex items-stretch gap-0 rounded-2xl overflow-hidden border-2 border-amber-400 dark:border-amber-500 shadow-md">
                    {/* Decrement button */}
                    <button
                      type="button"
                      onClick={handleDecrement}
                      className="flex items-center justify-center w-16 min-h-[56px] bg-gray-900 dark:bg-gray-700 hover:bg-gray-700 dark:hover:bg-gray-600 text-white text-3xl font-bold transition-colors select-none active:scale-95"
                      aria-label="Decrease quantity"
                    >
                      −
                    </button>
                    {/* Text input */}
                    <input
                      type="text"
                      inputMode="decimal"
                      value={quantityInput}
                      onChange={handleQuantityChange}
                      onBlur={handleBlur}
                      placeholder="0"
                      className="flex-1 text-center text-2xl font-bold bg-white dark:bg-gray-800 text-gray-900 dark:text-white border-0 outline-none min-h-[56px] px-2"
                      style={{ minWidth: 0 }}
                    />
                    {/* Increment button */}
                    <button
                      type="button"
                      onClick={handleIncrement}
                      className="flex items-center justify-center w-16 min-h-[56px] bg-amber-500 hover:bg-amber-400 text-white text-3xl font-bold transition-colors select-none active:scale-95"
                      aria-label="Increase quantity"
                    >
                      +
                    </button>
                  </div>
                  {quantityError && (
                    <p className="text-sm text-red-600 dark:text-red-400 mt-2 font-semibold flex items-center gap-1">
                      ⚠ {quantityError}
                    </p>
                  )}
                </div>

                {/* Quick Select Buttons */}
                <div className="flex flex-wrap gap-3">
                  <span className="text-sm text-gray-600 dark:text-gray-400 font-semibold mr-2 self-center">Quick Select:</span>
                  {[25, 50, 100, load?.quantity]?.map((qty) => (
                    qty <= load?.quantity && (
                      <Button
                        key={qty}
                        size="sm"
                        onClick={() => handleQuickSelect(qty)}
                        className="text-sm font-semibold h-10 px-5 border-2 bg-black text-white hover:bg-gray-800 border-black"
                      >
                        {qty} MT
                      </Button>
                    )
                  ))}
                </div>

                {/* Estimated Amount Preview */}
                {selectedQuantity > 0 && !quantityError && (
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-2 border-green-300 dark:border-green-700 rounded-2xl p-5 animate-fadeIn shadow-sm">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-base text-gray-700 dark:text-gray-300 font-semibold">Estimated Subtotal:</span>
                      <span className="text-2xl font-bold text-green-600 dark:text-green-400">{formatCurrency(load?.rate * selectedQuantity)}</span>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {selectedQuantity} MT × {formatCurrency(load?.rate)}/MT (excluding taxes)
                    </p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Order Summary */}
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 rounded-2xl p-5 border border-gray-200 dark:border-gray-700 shadow-sm">
                <h3 className="text-base font-bold text-gray-900 dark:text-white mb-4">Order Summary</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Offer ID:</span>
                    <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">{load?.offerId}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Mill:</span>
                    <span className="text-sm font-semibold text-gray-900 dark:text-white">{load?.millName}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Product:</span>
                    <span className="text-sm font-semibold text-gray-900 dark:text-white">{load?.product} ({load?.grade})</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Quantity:</span>
                    <span className="text-base font-bold text-green-600 dark:text-green-400">{selectedQuantity} MT</span>
                  </div>
                </div>
              </div>

              {/* Pricing Breakdown */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-2xl p-5 border-2 border-green-200 dark:border-green-800 shadow-sm">
                <h3 className="text-base font-bold text-gray-900 dark:text-white mb-4">Pricing Details</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Rate per Ton:</span>
                    <span className="text-sm font-semibold text-gray-900 dark:text-white">{formatCurrency(load?.rate)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Subtotal ({selectedQuantity} MT):</span>
                    <span className="text-base font-semibold text-gray-900 dark:text-white">{formatCurrency(subtotal)}</span>
                  </div>
                  <div className="flex justify-between items-center text-blue-600 dark:text-blue-400 pt-3 border-t-2 border-gray-200 dark:border-gray-700">
                    <span className="text-sm font-semibold">GST (5%):</span>
                    <span className="text-sm font-semibold">+{formatCurrency(gstAmount)}</span>
                  </div>
                  
                  {/* Insurance Toggle */}
                  {load?.insuranceAvailable && (
                    <div className="pt-3 border-t-2 border-gray-200 dark:border-gray-700">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm text-gray-600 dark:text-gray-400 font-semibold">
                          Transit Insurance (Optional):
                        </span>
                        <Button
                          size="sm"
                          onClick={handleInsuranceToggle}
                          className="text-sm h-10 px-4 font-semibold border-2 bg-black text-white hover:bg-gray-800 border-black"
                        >
                          {insuranceAdded ? '✓ Added' : '+ Add'}
                        </Button>
                      </div>
                      {insuranceAdded && (
                        <div className="bg-green-50 dark:bg-green-900/20 border-2 border-green-300 dark:border-green-700 rounded-xl p-3 text-sm animate-fadeIn">
                          <div className="flex justify-between items-center text-green-700 dark:text-green-400">
                            <span className="font-semibold">Insurance (0.5%):</span>
                            <span className="font-bold">+{formatCurrency(transitInsuranceAmount)}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex justify-between items-center pt-3 border-t-2 border-gray-200 dark:border-gray-700">
                    <span className="text-base font-bold text-gray-900 dark:text-white">Total Amount:</span>
                    <span className="text-2xl font-extrabold text-green-600 dark:text-green-400">{formatCurrency(totalAmount)}</span>
                  </div>
                </div>
              </div>

              {/* Terms */}
              <div className="bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-200 dark:border-amber-700 rounded-2xl p-4">
                <p className="text-xs text-amber-800 dark:text-amber-300 font-medium">
                  ⚠ By confirming, you agree to the purchase terms. Payment terms as per mutual agreement.
                  Cancellation charges may apply after confirmation.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          {step === 1 ? (
            <>
              <Button
                variant="outline"
                onClick={onClose}
                className="flex-1 h-12 text-base font-semibold border-2"
              >
                Cancel
              </Button>
              <Button
                onClick={handleProceedToReview}
                disabled={!selectedQuantity || !!quantityError}
                className="flex-1 h-12 text-base font-bold bg-amber-500 hover:bg-amber-600 text-white border-0 disabled:opacity-50"
              >
                {forTruck ? 'Proceed to Trucks →' : 'Proceed to Review →'}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={handleBack}
                className="flex-1 h-12 text-base font-semibold border-2"
              >
                ← Back
              </Button>
              <Button
                onClick={handleConfirm}
                className="flex-1 h-12 text-base font-bold bg-green-600 hover:bg-green-700 text-white border-0"
              >
                {forTruck ? 'Continue to Trucks →' : 'Confirm Purchase'}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BuySugarModal;